import { Autocomplete, Box, CircularProgress, TextField, Typography } from '@mui/material';
import { type FC, useMemo } from 'react';

import useTranslate from 'hooks/useTranslate';
import type { ServiceConsultant } from 'types';
import { getServiceConsultantName } from 'views/v2/adminPanel/helpers/serviceConsultants';

interface ConsultantSelectOption {
    id: number | null;
    name: string;
    email: string;
    isCompanyDefault: boolean;
}

interface DefaultConsultantSelectProps {
    companyDefaultConsultant: ServiceConsultant | null;
    consultants: ServiceConsultant[];
    disabled?: boolean;
    loading?: boolean;
    serviceName: string;
    value: number | null;
    onChange: (consultantId: number | null) => void | Promise<void>;
}

export const DefaultConsultantSelect: FC<DefaultConsultantSelectProps> = ({
    companyDefaultConsultant,
    consultants,
    disabled = false,
    loading = false,
    serviceName,
    value,
    onChange,
}) => {
    const { translate } = useTranslate();

    const options = useMemo<ConsultantSelectOption[]>(() => {
        const companyDefaultName = companyDefaultConsultant
            ? `${translate('companyDefault')} — ${getServiceConsultantName(
                  companyDefaultConsultant
              )}`
            : translate('noCompanyDefaultConsultant');

        return [
            {
                id: null,
                name: companyDefaultName,
                email: companyDefaultConsultant?.email ?? '',
                isCompanyDefault: true,
            },
            ...consultants
                .map((consultant) => ({
                    id: consultant.id,
                    name: getServiceConsultantName(consultant),
                    email: consultant.email,
                    isCompanyDefault: false,
                }))
                .sort((left, right) => left.name.localeCompare(right.name)),
        ];
    }, [companyDefaultConsultant, consultants, translate]);

    const selected = options.find((option) => option.id === value) ?? options[0];

    return (
        <Autocomplete
            fullWidth
            size="small"
            sx={{
                maxWidth: 360,
                '& .MuiInput-root': {
                    minHeight: 32,
                    px: 1,
                    py: 0.25,
                    borderRadius: 1,
                    transition: 'background-color 120ms ease',
                    '&:hover, &.Mui-focused': {
                        bgcolor: 'action.hover',
                    },
                },
                '& .MuiInput-root:before, & .MuiInput-root:after': {
                    display: 'none',
                },
                '& .MuiInputBase-input': {
                    py: '4px !important',
                    fontSize: '0.875rem',
                    lineHeight: 1.4,
                },
                '& .MuiAutocomplete-popupIndicator': {
                    p: 0.25,
                    mr: 0.25,
                    color: 'text.secondary',
                },
            }}
            options={options}
            value={selected}
            disabled={disabled}
            disableClearable
            loading={loading}
            getOptionLabel={(option) => option.name}
            isOptionEqualToValue={(option, selectedOption) => option.id === selectedOption.id}
            onChange={(_event, option) => void onChange(option.id)}
            renderOption={(props, option) => (
                <Box component="li" {...props} key={`${option.id ?? 'company-default'}`}>
                    <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {option.name}
                        </Typography>
                        {option.email ? (
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: 'block' }}
                            >
                                {option.email}
                            </Typography>
                        ) : null}
                        {option.isCompanyDefault ? (
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: 'block' }}
                            >
                                {translate('usesCompanyDefaultConsultant')}
                            </Typography>
                        ) : null}
                    </Box>
                </Box>
            )}
            renderInput={(params) => (
                <TextField
                    {...params}
                    variant="standard"
                    slotProps={{
                        ...params.slotProps,
                        input: {
                            ...params.slotProps.input,
                            endAdornment: (
                                <>
                                    {loading ? (
                                        <CircularProgress color="inherit" size={18} />
                                    ) : null}
                                    {params.slotProps.input.endAdornment}
                                </>
                            ),
                        },
                        htmlInput: {
                            ...params.slotProps.htmlInput,
                            'aria-label': `${translate('defaultConsultant')} ${serviceName}`,
                            title: selected.name,
                        },
                    }}
                />
            )}
        />
    );
};

export default DefaultConsultantSelect;
